import React, { useEffect, useRef, Fragment, useState } from "react";

import {
  useUserData,
  useUpdateUserData,
} from "components/customHooks/userDataContext";
import {
  logout,
  billingRefreshToken,
  providerRefreshToken,
} from "apiHandler/actions/auth";
import { Modal } from "@mui/material";

const ScreenLocker = (props) => {
  const userData = useUserData();
  const setUserData = useUpdateUserData();
  const refreshTokenTime = 30 * 60 * 1000; // Refresh token every hour
  const refreshIntervalRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const inactivityTimeoutRef = useRef(null);

  const INACTIVITY_TIMEOUT = 2 * 60 * 60 * 1000;

  // Function to handle token refresh
  const getRefreshToken = async () => {
    try {
      if (userData?.refresh_token && userData?.billing_refresh_token) {
        const {
          refresh_token,
          id_token,
          billing_refresh_token,
          billing_id_token,
        } = userData;

        const [billTokensResult, provTokensResult] = await Promise.all([
          billingRefreshToken(
            { email_id: userData.email, refresh_token },
            id_token,
          ),
          providerRefreshToken(
            { email_id: userData.email, refresh_token: billing_refresh_token },
            billing_id_token,
          ),
        ]);
        setUserData({
          ...userData,
          access_token: provTokensResult?.access_token,
          id_token: provTokensResult?.id_token,
          billing_access_token: billTokensResult?.billing_access_token,
          billing_id_token: billTokensResult?.billing_id_token,
          loginTime: new Date(),
        });
      } else {
        handleLogout();
      }
    } catch (error) {
      console.log("error: ", error);
      handleLogout();
    }
  };

  const handleLogout = () => {
    setUserData(null);
    logout();
  };

  const handleInactivity = () => {
    // Show modal after 2 hours of inactivity
    if (userData?.refresh_token) {
      setShowModal(true);
      clearTimeout(inactivityTimeoutRef.current);
    }
  };

  const resetInactivityTimer = () => {
    // Reset inactivity timer whenever there is activity
    if (userData?.refresh_token) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = setTimeout(
        handleInactivity,
        INACTIVITY_TIMEOUT,
      );
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    handleLogout();
  };

  const handleModalRedirect = () => {
    window.location.reload(); // Hard refresh the page
    handleLogout();
  };

  // useEffect to set up token refresh interval
  useEffect(() => {
    if (userData?.refresh_token) {
      refreshIntervalRef.current = setInterval(
        getRefreshToken,
        refreshTokenTime,
      );
      inactivityTimeoutRef.current = setTimeout(
        handleInactivity,
        INACTIVITY_TIMEOUT,
      );
    }

    // Attach event listeners to detect user activity
    window.addEventListener("mousemove", resetInactivityTimer);
    window.addEventListener("keydown", resetInactivityTimer);

    return () => {
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
      if (inactivityTimeoutRef.current)
        clearTimeout(inactivityTimeoutRef.current);
      window.removeEventListener("mousemove", resetInactivityTimer);
      window.removeEventListener("keydown", resetInactivityTimer);
    };
  }, [userData]);

  return (
    <Fragment>
      <div>{props.children}</div>

      <Modal open={showModal} onClose={handleModalClose}>
        <div className="session-timeout-modal">
          Your session has expired due to inactivity. Please Log in again to
          continue.
          <br />
          <button className="stm-btn" onClick={handleModalRedirect}>
            Login
          </button>
        </div>
      </Modal>
    </Fragment>
  );
};

export default ScreenLocker;
